import { supabase } from "@/integrations/supabase/client";
import { filterValidVideos } from "@/lib/utils";
import { Video } from "@/types/videos";
import { resolvePlaybackUrls } from "@/services/playbackUrls";

/**
 * Préparation du feed AVANT son affichage.
 *
 * Pourquoi : tant que l'authentification se résout, le réseau est inutilisé.
 * On en profite pour récupérer la LISTE de la première page, puis pour signer
 * les jetons de lecture des 3 premières vidéos SANS attendre le résultat : la
 * première vidéo n'a donc plus à attendre signature, manifeste puis fragment
 * après son affichage (écran noir à l'ouverture de l'accueil). Aucun fragment
 * n'est téléchargé ici. Le feed réutilise la liste au lieu de refaire la requête.
 */

export const FEED_PAGE_SIZE = 10;

/**
 * Sélection unique pour TOUTE vidéo destinée au lecteur (feed, recherche,
 * profil, page vidéo). Un select partiel ferait échouer `filterValidVideos`
 * et priverait le lecteur des champs Bunny (hls_url, provider, statut).
 */
export const PUBLISHED_VIDEO_SELECT = `
  *,
  profiles:user_id (
    id,
    full_name,
    username,
    avatar_url
  ),
  tags:video_tags (
    tag_id,
    tags (
      id,
      name,
      is_reserved,
      is_held
    )
  )
`;

/** Filtre appliqué en base pour exclure les vidéos Bunny encore en transcodage. */
const READY_OR_LEGACY_FILTER =
  "and(provider.neq.bunny,provider.neq.mux),processing_status.eq.ready,and(provider.neq.mux,legacy_video_url.not.is.null)";

export interface VideoPageResult {
  videos: Video[];
  hasMore: boolean;
}

/** Aplatit la structure des tags puis écarte les vidéos injouables. */
export function mapPublishedVideos(data: unknown[] | null): Video[] {
  if (!data || data.length === 0) return [];
  const withTags = data.map((video: any) => ({
    ...video,
    tags: video.tags?.map((vt: any) => vt.tags).filter(Boolean) || [],
  }));
  return filterValidVideos(withTags);
}

/** Récupère une vidéo publiée par son identifiant, prête pour le lecteur. */
export async function fetchPublishedVideoById(id: string): Promise<Video | null> {
  const { data } = await supabase
    .from("videos")
    .select(PUBLISHED_VIDEO_SELECT)
    .eq("id", id)
    .eq("is_published", true)
    .eq("moderation_status", "approved")
    .or(READY_OR_LEGACY_FILTER)
    .maybeSingle();

  if (!data) return null;
  return mapPublishedVideos([data])[0] ?? null;
}

/**
 * Récupère une vidéo publiée par son slug (URL publique lisible).
 * Le slug est unique en base : une seule ligne au maximum.
 */
export async function fetchPublishedVideoBySlug(slug: string): Promise<Video | null> {
  const { data } = await supabase
    .from("videos")
    .select(PUBLISHED_VIDEO_SELECT)
    .eq("slug", slug)
    .eq("is_published", true)
    .eq("moderation_status", "approved")
    .or(READY_OR_LEGACY_FILTER)
    .maybeSingle();

  if (!data) return null;
  return mapPublishedVideos([data])[0] ?? null;
}


/**
 * Récupère une page de vidéos publiées.
 *
 * Le tri (boost actif → engagement → date) est calculé EN BASE par la RPC
 * `get_feed_videos` AVANT le LIMIT : une vidéo boostée hors du top engagement
 * peut donc réellement remonter en première position. La RPC ne renvoie que
 * des identifiants ordonnés ; on hydrate ensuite avec le select complet du
 * lecteur en réappliquant l'ordre serveur (`.in()` ne le garantit pas).
 */
export async function fetchVideoPage(offset: number): Promise<VideoPageResult> {
  const { data: ranked, error } = await supabase.rpc("get_feed_videos", {
    p_offset: offset,
    p_limit: FEED_PAGE_SIZE,
  });

  if (error || !ranked || ranked.length === 0) return { videos: [], hasMore: false };

  const orderedIds = ranked.map((row: { id: string }) => row.id);

  // Signature des 3 premières en parallèle de l'hydratation : la 1re vidéo
  // gagne un aller-retour réseau. resolvePlaybackUrls déduplique les appels.
  if (offset === 0) void resolvePlaybackUrls(orderedIds.slice(0, 3));

  const { data } = await supabase
    .from("videos")
    .select(PUBLISHED_VIDEO_SELECT)
    .in("id", orderedIds)
    .eq("is_published", true)
    .eq("moderation_status", "approved");

  if (!data || data.length === 0) return { videos: [], hasMore: ranked.length === FEED_PAGE_SIZE };

  const byId = new Map(mapPublishedVideos(data).map((v) => [v.id, v]));
  const videos = orderedIds
    .map((id) => byId.get(id))
    .filter((v): v is Video => Boolean(v));

  return {
    videos,
    hasMore: ranked.length === FEED_PAGE_SIZE,
  };
}

/**
 * Filet de sécurité : réordonne une liste déjà chargée selon la même règle
 * métier que la RPC (boost actif → engagement → date). Utilisé pour les
 * listes qui ne passent pas par le feed (recherche par mot-clé / par tag).
 */
export async function sortVideosByBoost(videos: Video[]): Promise<Video[]> {
  if (videos.length === 0) return videos;
  const boostMap = await fetchBoostMap(videos.map((v) => v.id));
  return sortByBoostMap(videos, boostMap);
}

/**
 * Scores de boost via RPC dédiée : les montants en Coco ne sont jamais
 * exposés côté client, seul le score agrégé par vidéo est renvoyé.
 */
export async function fetchBoostMap(ids: string[]): Promise<Map<string, number>> {
  const boostMap = new Map<string, number>();
  if (ids.length === 0) return boostMap;
  const { data: boostScores, error } = await supabase.rpc("get_active_video_boost_scores", {
    p_video_ids: ids,
  });
  if (error) console.error("Lecture des boosts impossible:", error);
  for (const row of (boostScores as { video_id: string; boost_score: number }[] | null) ?? []) {
    boostMap.set(row.video_id, Number(row.boost_score) || 0);
  }
  return boostMap;
}

/** Tri métier : boost actif → engagement → date. */
export function sortByBoostMap(videos: Video[], boostMap: Map<string, number>): Video[] {
  return [...videos].sort((a, b) => {
    const boostDiff = (boostMap.get(b.id) || 0) - (boostMap.get(a.id) || 0);
    if (boostDiff !== 0) return boostDiff;

    const aEngagement = (a as any).engagement_score || 0;
    const bEngagement = (b as any).engagement_score || 0;
    if (bEngagement !== aEngagement) return bEngagement - aEngagement;
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  });
}


/** Cache module de la première page préparée pendant l'écran d'authentification. */
let prefetchPromise: Promise<VideoPageResult> | null = null;
let prefetchedAt = 0;

/** Au-delà de 60 s, la page préparée est considérée périmée. */
const PREFETCH_TTL_MS = 60_000;

/**
 * Prépare la première page en arrière-plan (idempotent).
 * Coût négligeable si l'utilisateur n'ouvre jamais le feed.
 */
export function prefetchFirstFeedPage(): void {
  if (prefetchPromise && Date.now() - prefetchedAt < PREFETCH_TTL_MS) return;

  prefetchedAt = Date.now();
  prefetchPromise = (async () => {
    // La signature des 3 premières part déjà depuis fetchVideoPage(0).
    return fetchVideoPage(0);
  })();
  prefetchPromise.catch(() => {
    prefetchPromise = null;
  });
}

/** Récupère la page préparée (et la consomme) ou `null` si absente/périmée. */
export function takePrefetchedFirstPage(): Promise<VideoPageResult> | null {
  if (!prefetchPromise || Date.now() - prefetchedAt > PREFETCH_TTL_MS) return null;
  const promise = prefetchPromise;
  prefetchPromise = null;
  return promise;
}

/** Nombre total de vidéos visibles par un visiteur non connecté (aperçu). */
export const GUEST_TEASER_SIZE = 7;

/**
 * Playlist d'aperçu pour un visiteur non connecté arrivant par un lien de
 * partage : la vidéo du lien puis des vidéos de la même thématique.
 * On complète avec des publications récentes s'il n'y a pas assez de tags.
 */
export async function fetchTeaserPlaylist(seed: Video): Promise<Video[]> {
  const playlist: Video[] = [seed];
  const seen = new Set<string>([seed.id]);

  const pushAll = (videos: Video[]) => {
    for (const video of videos) {
      if (playlist.length >= GUEST_TEASER_SIZE) return;
      if (seen.has(video.id)) continue;
      seen.add(video.id);
      playlist.push(video);
    }
  };

  // 1) Même thématique : on se limite aux 2 premiers tags de la vidéo du lien.
  const tagIds = (seed.tags ?? []).slice(0, 2).map((tag: any) => tag.id).filter(Boolean);
  if (tagIds.length > 0) {
    const { data: tagged } = await supabase
      .from("video_tags")
      .select("video_id")
      .in("tag_id", tagIds)
      .limit(50);

    const relatedIds = [...new Set((tagged ?? []).map((row: any) => row.video_id))].filter(
      (id) => id !== seed.id,
    );

    if (relatedIds.length > 0) {
      const { data } = await supabase
        .from("videos")
        .select(PUBLISHED_VIDEO_SELECT)
        .in("id", relatedIds)
        .eq("is_published", true)
        .eq("moderation_status", "approved")
        .or(READY_OR_LEGACY_FILTER)
        .order("created_at", { ascending: false })
        .limit(GUEST_TEASER_SIZE);
      pushAll(mapPublishedVideos(data));
    }
  }

  // 2) Complément : publications récentes (jamais de vidéo privée).
  if (playlist.length < GUEST_TEASER_SIZE) {
    const { data } = await supabase
      .from("videos")
      .select(PUBLISHED_VIDEO_SELECT)
      .eq("is_published", true)
      .eq("moderation_status", "approved")
      .or(READY_OR_LEGACY_FILTER)
      .order("created_at", { ascending: false })
      .limit(GUEST_TEASER_SIZE * 2);
    pushAll(mapPublishedVideos(data));
  }

  return playlist;
}
